import type { CalcCheck, CalcReaction, CalcResult, CalcSheet, CalcStructuredValue } from "../../../types/calc";
import { CALC_EXPORT_COPY } from "../../../api/calcExportCopy";
import { prettyUnits } from "../calcDerive";
import {
  LOAD_CALC_TEMPLATE_BY_ID,
  loadTemplateIdFromCalculatorId,
} from "../loadCalcDefinitions";
import type { LoadCalcValues } from "../loadCalcTypes";
import { LoadVisual } from "../LoadCalcWorkspace";
import {
  CalculatorSchematic,
  SCHEMATIC_CALCULATOR_IDS,
} from "../CalculatorSchematic";

export type CalcExportSnapshot = {
  schemaVersion: string;
  exportId: string;
  generatedAt: string;
  scope: "calculation" | "project";
  mode: "simplified" | "detailed";
  options: { includeCover: boolean; includeToc: boolean; includeDraftLoads: boolean };
  project: {
    id: string;
    name: string;
    address: string;
    description?: string;
    jurisdiction?: string;
    jobNumber?: string | null;
    client?: string | null;
    engineer?: string | null;
    license?: string | null;
    buildingCode?: string | null;
    riskCategory?: string | null;
    deflectionLive?: string | null;
    deflectionTotal?: string | null;
    designMethod?: string | null;
  };
  grouping: {
    mode?: "manual" | "automatic";
    groups?: Array<{ id: string; label: string }>;
    assignments?: Record<string, string>;
  };
  sheets: CalcSheet[];
};

type RegistryEntry = {
  title: string;
  family: "member" | "connection" | "loading";
  standard: string;
};

export const CALC_EXPORT_REGISTRY: Record<string, RegistryEntry> = {
  wood_beam_asd: { title: "Wood Beam", family: "member", standard: "NDS 2024" },
  wood_column_asd: { title: "Wood Column", family: "member", standard: "NDS 2024" },
  wood_tension_asd: { title: "Wood Tension Member", family: "member", standard: "NDS 2024" },
  wood_stud_wall_asd: { title: "Wood Stud Wall", family: "member", standard: "NDS 2024" },
  wood_scl_beam_asd: { title: "SCL Beam", family: "member", standard: "NDS 2024" },
  wood_biaxial_beam_column_asd: { title: "Biaxial Beam-Column", family: "member", standard: "NDS 2024" },
  glulam_beam_asd: { title: "Glulam Beam", family: "member", standard: "NDS 2024" },
  wood_ijoist_asd: { title: "Wood I-Joist", family: "member", standard: "Manufacturer / NDS 2024" },
  wood_nail_connection_asd: { title: "Wood Nail / Spike Connection", family: "connection", standard: "NDS 2024" },
  wood_bolt_connection_asd: { title: "Wood Bolt Connection", family: "connection", standard: "NDS 2024" },
  wood_lag_connection_asd: { title: "Wood Lag Screw Connection", family: "connection", standard: "NDS 2024" },
  wood_screw_connection_asd: { title: "Wood Screw Connection", family: "connection", standard: "NDS 2024" },
  connector_product_capacity: { title: "Connector Product Capacity", family: "connection", standard: "Published ESR / manufacturer capacity" },
  steel_beam_aisc: { title: "Steel Beam", family: "member", standard: "ANSI/AISC 360-22" },
  steel_column_aisc: { title: "Steel Column", family: "member", standard: "ANSI/AISC 360-22" },
  cfs_joist: { title: "Cold-Formed Steel Joist", family: "member", standard: "AISI S100/S240 · SFIA v2025.1" },
  cfs_stud_wall: { title: "Cold-Formed Steel Stud Wall", family: "member", standard: "AISI S100/S240 · SFIA v2025.1" },
  cfs_header: { title: "Cold-Formed Steel Header", family: "member", standard: "AISI S100 · SFIA v2025.1" },
  concrete_beam_aci: { title: "Concrete Beam", family: "member", standard: "ACI 318-19(22)" },
  continuous_footing: { title: "Continuous Footing", family: "member", standard: "ACI 318-19(22)" },
  pad_footing: { title: "Pad Footing", family: "member", standard: "ACI 318-19(22)" },
  masonry_bearing_wall_tms: { title: "Masonry Out-of-Plane Wall", family: "member", standard: "2011 MSJC ASD / 2012 IBC" },
  masonry_shear_wall_tms: { title: "Masonry Shear Wall", family: "member", standard: "TMS 402-16 / TMS 602-16" },
  masonry_lintel_tms: { title: "Masonry Lintel", family: "member", standard: "2011 MSJC ASD / 2012 IBC" },
  proprietary_hanger_connection: { title: "Proprietary Hanger Capacity Check", family: "connection", standard: "Manufacturer evaluation report" },
  proprietary_hold_down_connection: { title: "Proprietary Hold-Down Load Path", family: "connection", standard: "Manufacturer report / qualified downstream checks" },
  section_properties: { title: "Section Properties", family: "member", standard: "Geometric identities" },
};

for (const [id, template] of Object.entries(LOAD_CALC_TEMPLATE_BY_ID)) {
  CALC_EXPORT_REGISTRY[`load_${id}`] = { title: template.name, family: "loading", standard: template.standard };
}

function clean(value: unknown): string {
  if (value === null || value === undefined || value === "") return "-";
  return String(value)
    .replace(/[\u2010-\u2015]/g, "-")
    .replace(/\u2212/g, "-")
    .replace(/\u00b7/g, " / ");
}

function humanize(value: string): string {
  return clean(value)
    .replace(/^_+/, "")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function formatValue(value: unknown): string {
  if (typeof value === "number") {
    if (!Number.isFinite(value)) return "-";
    return new Intl.NumberFormat("en-US", { maximumFractionDigits: 3 }).format(value);
  }
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) return value.map(formatValue).join(" / ");
  if (value && typeof value === "object") {
    return Object.entries(value as Record<string, unknown>)
      .map(([key, item]) => `${clean(key)} ${formatValue(item)}`)
      .join(" / ");
  }
  return clean(value);
}

// Sentence-case label - mirrors the app's readableKey so the export and the
// on-screen sheet share one label grammar (limit states, supports, governing
// states are sentence case, never Title Case).
function sentenceLabel(value: unknown): string {
  const text = clean(value);
  if (text === "-") return text;
  const spaced = text
    .replace(/^_+/, "")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim();
  return spaced ? spaced.charAt(0).toUpperCase() + spaced.slice(1) : text;
}

// Value label layer: engine identifiers (floor_live, member_db) humanize at
// render; audit-trail keys/ids and real prose pass through untouched, and
// unit columns pick up the app's unit typesetting.
const SNAKE_IDENTIFIER = /^[a-z][a-z0-9]*(?:_[a-z0-9]+)+$/;
const KEYLIKE_NAME = /(^|_)(key|id)s?$/i;
const VALUE_LABELS: Record<string, string> = {
  member_db: "Member database",
  resolved_catalog: "Resolved catalog",
};
function displayValue(name: string, value: unknown): string {
  if (typeof value === "string") {
    if (/^units?$/i.test(name)) return prettyUnits(value);
    if (!KEYLIKE_NAME.test(name) && SNAKE_IDENTIFIER.test(value)) {
      return VALUE_LABELS[value] ?? sentenceLabel(value);
    }
  }
  // D/C canon: utilization/DCR table columns print percent (one decimal),
  // never 0.62-style ratios.
  if (typeof value === "number" && /^(utilization|dcr)$/i.test(name)) {
    return utilization1(value);
  }
  return formatValue(value);
}

function utilization(value: number | null | undefined) {
  return value == null ? "-" : `${Math.round(value * 100)}%`;
}

function utilization1(value: number | null | undefined) {
  return value == null ? "-" : `${(value * 100).toFixed(1)}%`;
}

function statusLabel(sheet: CalcSheet) {
  const load = sheet.results?.structured?.loadCalculation;
  if (load) return humanize(load.status);
  if (sheet.results?.structured?.calculationKind === "section_analysis") return "Calculated";
  if (sheet.passes === true) return "Pass";
  if (sheet.passes === false) return "Fail";
  return humanize(sheet.status || "Draft");
}

function statusTone(sheet: CalcSheet) {
  const label = statusLabel(sheet).toLowerCase();
  if (label === "pass" || label === "complete") return "pass";
  if (label === "fail" || label === "blocked" || label === "error") return "fail";
  return "notice";
}

function Metric({ label, value, detail, tone }: { label: string; value: string; detail?: string; tone?: string }) {
  return (
    <div className={`ce-metric${tone ? ` is-${tone}` : ""}`}>
      <span>{clean(label)}</span>
      <strong>{clean(value)}</strong>
      {detail ? <small>{clean(detail)}</small> : null}
    </div>
  );
}

function MemberDiagram({ sheet }: { sheet: CalcSheet }) {
  const reference = sheet.results?.structured?.reference;
  const b = reference?.bIn ?? 1.5;
  const d = reference?.dIn ?? 9.25;
  const ratio = Math.max(.18, Math.min(.62, Number(b) / Math.max(Number(d), .1)));
  const width = 128 * ratio;
  return (
    <svg viewBox="0 0 360 190" className="ce-diagram" role="img" aria-label="Member section and span">
      <g fill="none" stroke="currentColor" strokeWidth="2">
        <line x1="30" y1="142" x2="330" y2="142" />
        <path d="M48 142 l-12 18 h24 z M312 142 l-12 18 h24 z" />
        <line x1="48" y1="128" x2="48" y2="145" />
        <line x1="312" y1="128" x2="312" y2="145" />
        <rect x={180 - width / 2} y="24" width={width} height="82" fill="var(--ce-soft)" />
        <line x1={180 + width / 2 + 14} y1="24" x2={180 + width / 2 + 14} y2="106" />
      </g>
      <text x="180" y="18" textAnchor="middle">{clean(sheet.callout || reference?.section || "Member section")}</text>
      <text x="180" y="182" textAnchor="middle">{sheet.results?.structured?.diagram?.spanIn ? `${formatValue(sheet.results.structured.diagram.spanIn / 12)} ft span` : "Member geometry"}</text>
      <text x={180 + width / 2 + 20} y="68">d = {formatValue(d)} in</text>
      <text x="180" y="122" textAnchor="middle">b = {formatValue(b)} in</text>
    </svg>
  );
}

function ConnectionDiagram({ sheet }: { sheet: CalcSheet }) {
  const count = Math.max(2, Math.min(7, Number(sheet.inputs?.number_of_fasteners ?? sheet.inputs?.fastener_count ?? 4)));
  return (
    <svg viewBox="0 0 360 190" className="ce-diagram" role="img" aria-label="Connection schematic">
      <rect x="42" y="44" width="178" height="98" className="ce-svg-member" />
      <rect x="140" y="64" width="178" height="98" className="ce-svg-member is-second" />
      {Array.from({ length: count }, (_, index) => (
        <circle key={index} cx={165 + (index % 4) * 31} cy={89 + Math.floor(index / 4) * 38} r="7" className="ce-svg-fastener" />
      ))}
      <path d="M25 93 h54 M25 93 l16 -9 M25 93 l16 9" className="ce-svg-force" />
      <path d="M335 118 h-54 M335 118 l-16 -9 M335 118 l-16 9" className="ce-svg-force" />
      <text x="180" y="181" textAnchor="middle">{count} fasteners / connection demand</text>
    </svg>
  );
}

function ResultVisual({ sheet }: { sheet: CalcSheet }) {
  const loadId = loadTemplateIdFromCalculatorId(sheet.calculatorId);
  if (loadId) {
    const template = LOAD_CALC_TEMPLATE_BY_ID[loadId];
    return <div className="ce-load-visual"><LoadVisual template={template} values={(sheet.inputs ?? {}) as LoadCalcValues} unknownMissing /></div>;
  }
  if (SCHEMATIC_CALCULATOR_IDS.has(sheet.calculatorId)) {
    return <CalculatorSchematic calculatorId={sheet.calculatorId} values={sheet.inputs ?? {}} />;
  }
  const family = CALC_EXPORT_REGISTRY[sheet.calculatorId]?.family;
  return family === "connection" ? <ConnectionDiagram sheet={sheet} /> : <MemberDiagram sheet={sheet} />;
}

function InputSummary({ sheet, detailed }: { sheet: CalcSheet; detailed: boolean }) {
  const entries = Object.entries(sheet.inputs ?? {}).filter(([key, value]) => !key.startsWith("_") && !Array.isArray(value));
  const visible = detailed ? entries : entries.slice(0, 10);
  const arrayEntries = Object.entries(sheet.inputs ?? {}).filter(([, value]) => Array.isArray(value));
  return (
    <section className="ce-section ce-input-summary">
      <h3>Inputs</h3>
      <div className="ce-kv-grid">
        {visible.map(([key, value]) => (
          <div key={key}><span>{humanize(key)}</span><strong>{displayValue(key, value)}</strong></div>
        ))}
      </div>
      {detailed ? arrayEntries.map(([key, rows]) => <ObjectTable key={key} title={humanize(key)} rows={rows as Array<Record<string, unknown>>} />) : null}
      {!detailed && entries.length > visible.length ? <p className="ce-more-note">Additional inputs are retained in the detailed report.</p> : null}
    </section>
  );
}

function ObjectTable({ title, rows }: { title: string; rows: Array<Record<string, unknown>> }) {
  const columns = Array.from(new Set(rows.flatMap((row) => Object.keys(row)))).slice(0, 10);
  if (!rows.length || !columns.length) return null;
  return (
    <div className="ce-table-block">
      <h4>{clean(title)}</h4>
      <table><thead><tr>{columns.map((column) => <th key={column}>{humanize(column)}</th>)}</tr></thead>
        <tbody>{rows.map((row, index) => <tr key={index}>{columns.map((column) => <td key={column}>{displayValue(column, row[column])}</td>)}</tr>)}</tbody>
      </table>
    </div>
  );
}

// Design-record support reactions print in the sheet panel's shape: one row
// per support, service-level unfactored values by ASCE load type. Design
// records carry no factored envelope (maxLb is honestly null), so the generic
// object dump would print a null column that reads as missing data.
const REACTION_TYPE_ORDER = ["D", "L", "Lr", "S", "R", "Di", "W_down", "W_up"];

function ReactionsTable({ rows, envelope = false }: { rows: CalcReaction[]; envelope?: boolean }) {
  const present = new Set<string>();
  for (const row of rows) for (const type of Object.keys(row.byType ?? {})) present.add(type);
  const types = [
    ...REACTION_TYPE_ORDER.filter((type) => present.has(type)),
    ...[...present].filter((type) => !REACTION_TYPE_ORDER.includes(type)).sort(),
  ];
  const hasPositions = rows.some((row) => row.positionIn != null);
  const hasEnvelope = envelope && rows.some((row) => row.maxLb != null);
  const hasBearing = envelope && rows.some((row) => row.minBearingLenIn != null);
  return (
    <div className="ce-table-block">
      <table>
        <thead><tr>
          <th>Support</th>
          {hasPositions ? <th>Position <small>in</small></th> : null}
          {hasEnvelope ? <th>Reaction <small>lb</small></th> : null}
          {hasBearing ? <th>Min bearing <small>in</small></th> : null}
          {types.length
            ? types.map((type) => <th key={type}>{type} <small>lb</small></th>)
            : !hasEnvelope ? <th>Reaction <small>lb</small></th> : null}
        </tr></thead>
        <tbody>{rows.map((row, index) => (
          <tr key={`${row.label}-${index}`}>
            <td>{sentenceLabel(row.label)}</td>
            {hasPositions ? <td>{formatValue(row.positionIn)}</td> : null}
            {hasEnvelope ? <td>{formatValue(row.maxLb)}</td> : null}
            {hasBearing ? <td>{formatValue(row.minBearingLenIn)}</td> : null}
            {types.length
              ? types.map((type) => <td key={type}>{formatValue(row.byType?.[type])}</td>)
              : !hasEnvelope ? <td>-</td> : null}
          </tr>
        ))}</tbody>
      </table>
      <p className="ce-more-note">
        {envelope
          ? "Factored combination envelope per support; minimum bearing is the length required at full utilization."
          : types.length
            ? "Service-level unfactored reactions by ASCE load type; linked connection checks consume these and re-combine them under their own design method."
            : "No service load entered; every load type resolves to zero at each support."}
      </p>
    </div>
  );
}

function ChecksTable({ checks, compact = false }: { checks: CalcCheck[]; compact?: boolean }) {
  if (!checks.length) return <p className="ce-empty">No limit-state checks were returned.</p>;
  const rows = compact ? checks.slice(0, 7) : checks;
  return (
    <table className="ce-checks"><thead><tr><th>Limit state</th><th>Status</th><th>D/C</th><th>Demand</th><th>Capacity</th><th>Reference</th></tr></thead>
      <tbody>{rows.map((check, index) => (
        <tr key={`${check.limitState}-${index}`}>
          <td>{sentenceLabel(check.limitState)}</td>
          <td><span className={`ce-status is-${check.passes ? "pass" : "fail"}`}>{check.passes ? "Pass" : "Fail"}</span></td>
          <td>{utilization1(check.dcr)}</td>
          <td>{formatValue(check.demand)} {check.units ? prettyUnits(String(check.units)) : ""}</td>
          <td>{formatValue(check.capacity)} {check.units ? prettyUnits(String(check.units)) : ""}</td>
          <td>{clean(check.ndsRef)}</td>
        </tr>
      ))}</tbody>
    </table>
  );
}

function MemberPrimaryResults({ sheet, compact }: { sheet: CalcSheet; compact: boolean }) {
  const structured = sheet.results?.structured;
  if (!structured) return <p className="ce-empty">No computed result is available.</p>;
  if (structured.calculationKind === "section_analysis") {
    // Analysis calculators (section properties) have no verdict by design —
    // the calculated property set IS the primary result, never a checks table.
    const properties = Object.entries(structured.designCalculation?.intermediates ?? {});
    return (
      <>
        <section className="ce-section">
          <h3>Calculated section properties</h3>
          {properties.length ? (
            <div className="ce-kv-grid">
              {properties.map(([key, value]) => (
                <div key={key}><span>{humanize(key)}</span><strong>{displayValue(key, value)}</strong></div>
              ))}
            </div>
          ) : <p className="ce-empty">No calculated properties are available.</p>}
        </section>
        {compact ? <Notices result={sheet.results} detailed={false} /> : null}
      </>
    );
  }
  return (
    <>
      <section className="ce-section">
        <h3>Design checks</h3>
        <ChecksTable checks={structured.checks ?? []} compact={compact} />
      </section>
      {structured.reactions?.length ? (
        <section className="ce-section"><h3>Support reactions</h3>
          {structured.mode === "design_calculation"
            ? <ReactionsTable rows={structured.reactions} />
            : <ReactionsTable rows={structured.reactions} envelope />}
        </section>
      ) : null}
      {!compact ? null : <Notices result={sheet.results} detailed={false} />}
    </>
  );
}

function MemberDetailedAppendix({ sheet }: { sheet: CalcSheet }) {
  const structured = sheet.results?.structured;
  if (!structured) return null;
  const design = structured.designCalculation;
  // section_analysis prints its property set as the PRIMARY result; repeating
  // the same intermediates as a "trace" here would just duplicate the page.
  const skipIntermediates = structured.calculationKind === "section_analysis";
  return (
    <>
      {!skipIntermediates && design && Object.keys(design.intermediates ?? {}).length ? (
        <section className="ce-section"><h3>Calculation trace</h3><div className="ce-kv-grid">{Object.entries(design.intermediates ?? {}).map(([key, value]) => (
          <div key={key}><span>{humanize(key)}</span><strong>{displayValue(key, value)}</strong></div>
        ))}</div></section>
      ) : null}
      {design ? Object.entries(design.tables ?? {}).map(([title, rows]) => (
        <section className="ce-section" key={title}><h3>{clean(title)}</h3><ObjectTable title={title} rows={rows as Array<Record<string, unknown>>} /></section>
      )) : null}
      {structured.comboTables?.map((table, index) => (
        <section className="ce-section" key={index}><h3>{clean(table.label ?? table.key ?? "Load combinations")}</h3>
          <ObjectTable title="Combination capacity" rows={(table.rows ?? []) as unknown as Array<Record<string, unknown>>} />
        </section>
      ))}
      {structured.factors?.length ? (
        <section className="ce-section"><h3>Adjustment factors</h3><ObjectTable title="Factor ledger" rows={structured.factors as unknown as Array<Record<string, unknown>>} /></section>
      ) : null}
      {structured.reference ? (
        <section className="ce-section"><h3>Section and material properties</h3>
          <div className="ce-kv-grid">{Object.entries(structured.reference).filter(([, value]) => typeof value !== "object").map(([key, value]) => (
            <div key={key}><span>{humanize(key)}</span><strong>{displayValue(key, value)}</strong></div>
          ))}</div>
          {structured.reference.details ? <ObjectTable title="Reference details" rows={[structured.reference.details as Record<string, unknown>]} /> : null}
        </section>
      ) : null}
      <Notices result={sheet.results} detailed />
    </>
  );
}

function LoadingResults({ sheet, detailed, part = "all" }: { sheet: CalcSheet; detailed: boolean; part?: "all" | "primary" | "appendix" }) {
  const result = sheet.results?.structured?.loadCalculation;
  if (!result) return <div className="ce-draft-callout"><strong>Draft load calculation</strong><span>No authoritative engine result is available. Inputs are shown for review only.</span></div>;
  const tableEntries = Object.entries(result.tables ?? {});
  const visibleTables = detailed ? tableEntries : tableEntries.slice(0, 1);
  return (
    <>
      {part !== "appendix" ? <section className="ce-section">
        <h3>Result tables</h3>
        {visibleTables.length ? visibleTables.map(([key, rows]) => <ObjectTable key={key} title={humanize(key)} rows={(detailed ? rows : rows.slice(0, 6)) as Array<Record<string, unknown>>} />) : <p className="ce-empty">No result rows were returned.</p>}
      </section> : null}
      {part !== "primary" && detailed && Object.keys(result.intermediates ?? {}).length ? (
        <section className="ce-section"><h3>Calculation trace</h3><div className="ce-kv-grid">{Object.entries(result.intermediates ?? {}).map(([key, value]) => <div key={key}><span>{clean(key)}</span><strong>{formatValue(value)}</strong></div>)}</div></section>
      ) : null}
      {part !== "primary" ? <NoticeGroups groups={[
        ["Warnings", result.warnings ?? []], ["Assumptions", result.assumptions ?? []],
        ["Deferred and unsupported", result.deferred ?? []], ["Notes", result.notes ?? []],
        ["Sources", result.sources ?? []],
      ]} detailed={detailed} /> : null}
    </>
  );
}

function Notices({ result, detailed }: { result: CalcResult | null | undefined; detailed: boolean }) {
  const structured = result?.structured;
  const design = structured?.designCalculation;
  const limitations = design?.limitations ?? [];
  const warnings = (structured?.warnings ?? []).filter(
    (warning) => !limitations.some((limitation) => warning === `Scope limitation: ${limitation}`),
  );
  return <NoticeGroups groups={[
    ["Warnings", warnings],
    ["Assumptions", structured?.assumptions ?? []],
    ["Scope and limitations", limitations],
    ["Notes", design?.notes ?? []],
    ["Sources", design?.sources ?? []],
  ]} detailed={detailed} />;
}

function NoticeGroups({ groups, detailed }: { groups: Array<[string, readonly string[]]>; detailed: boolean }) {
  const populated = groups.filter(([, items]) => items.length);
  if (!populated.length) return null;
  return (
    <div className="ce-notice-grid">{populated.map(([title, items]) => (
      <section className="ce-section" key={title}><h3>{title}</h3><ul>{(detailed ? items : items.slice(0, 3)).map((item, index) => <li key={index}>{clean(item)}</li>)}</ul></section>
    ))}</div>
  );
}

function SheetHeader({ snapshot, sheet }: { snapshot: CalcExportSnapshot; sheet: CalcSheet }) {
  const registry = CALC_EXPORT_REGISTRY[sheet.calculatorId] ?? { title: humanize(sheet.calculatorId), family: "member", standard: "" };
  return (
    <header className="ce-sheet-head">
      <div className="ce-brand"><strong>FIRMARK</strong><span>Calculation report</span></div>
      <div className="ce-sheet-identity"><span>Mark</span><strong>{clean(sheet.name || "Untitled")}</strong><span>{registry.title}</span></div>
      <div className="ce-sheet-meta"><strong>{registry.standard}</strong><span>{clean(snapshot.project.jobNumber || snapshot.project.name)}</span></div>
    </header>
  );
}

function SheetMetrics({ sheet }: { sheet: CalcSheet }) {
  const load = sheet.results?.structured?.loadCalculation;
  const structured = sheet.results?.structured;
  return (
    <div className="ce-metrics">
      <Metric label="Status" value={statusLabel(sheet)} tone={statusTone(sheet)} />
      <Metric label="Governing" value={load?.governingLabel ? clean(load.governingLabel) : sentenceLabel(sheet.governing || "-")} detail={load?.governingValue ?? undefined} />
      <Metric label={load ? "Result" : "Maximum D/C"} value={load?.governingValue ? clean(load.governingValue) : utilization(sheet.maxDcr)} />
      <Metric label="Basis" value={clean(structured?.designMethod || sheet.designMethod || "-")} detail={structured?.ndsEdition ?? undefined} />
    </div>
  );
}

function CalcExportSheet({ snapshot, sheet }: { snapshot: CalcExportSnapshot; sheet: CalcSheet }) {
  const detailed = snapshot.mode === "detailed";
  const isLoad = Boolean(loadTemplateIdFromCalculatorId(sheet.calculatorId));
  const isConnection = CALC_EXPORT_REGISTRY[sheet.calculatorId]?.family === "connection";
  const inputArrayRows = Object.values(sheet.inputs ?? {}).reduce<number>(
    (total, value) => total + (Array.isArray(value) ? value.length : 0),
    0,
  );
  const loadNeedsContinuation = detailed && isLoad && inputArrayRows > 4;
  const primary = (
    <article className={`ce-sheet is-${snapshot.mode}`} data-sheet-id={sheet.id} data-one-page={detailed ? undefined : "true"}>
      <SheetHeader snapshot={snapshot} sheet={sheet} />
      <SheetMetrics sheet={sheet} />
      <div className="ce-sheet-lead">
        <section className="ce-section ce-visual"><h3>{isLoad ? "Geometry and loading" : isConnection ? "Connection model" : "Member and design model"}</h3><ResultVisual sheet={sheet} /></section>
        <InputSummary sheet={sheet} detailed={detailed} />
      </div>
      {isLoad ? <LoadingResults sheet={sheet} detailed={detailed} part={loadNeedsContinuation ? "primary" : "all"} /> : <MemberPrimaryResults sheet={sheet} compact={!detailed} />}
      <footer className="ce-sheet-foot">
        <span>{clean(snapshot.project.name)} / {clean(sheet.name)}</span>
        <span>Generated {new Date(snapshot.generatedAt).toLocaleDateString("en-US")}</span>
        <span>{sheet.peStamped ? CALC_EXPORT_COPY.reviewedCalculation : CALC_EXPORT_COPY.forEngineeringReview}</span>
      </footer>
    </article>
  );
  if (!detailed) return primary;
  if (isLoad) {
    if (!loadNeedsContinuation) return primary;
    return (
      <>
        {primary}
        <article className="ce-sheet is-detailed ce-continuation" data-sheet-id={`${sheet.id}-detail`}>
          <SheetHeader snapshot={snapshot} sheet={sheet} />
          <div className="ce-continuation-title"><strong>Calculation trace and sources</strong><span>Intermediate values, assumptions, warnings, and deferred conditions</span></div>
          <LoadingResults sheet={sheet} detailed part="appendix" />
          <footer className="ce-sheet-foot"><span>{clean(snapshot.project.name)} / {clean(sheet.name)} / detail</span><span>Generated {new Date(snapshot.generatedAt).toLocaleDateString("en-US")}</span><span>{CALC_EXPORT_COPY.forEngineeringReview}</span></footer>
        </article>
      </>
    );
  }
  return (
    <>
      {primary}
      <article className="ce-sheet is-detailed ce-continuation" data-sheet-id={`${sheet.id}-detail`}>
        <SheetHeader snapshot={snapshot} sheet={sheet} />
        <div className="ce-continuation-title"><strong>Detailed calculation record</strong><span>Load combinations, factors, properties, and source notes</span></div>
        <MemberDetailedAppendix sheet={sheet} />
        <footer className="ce-sheet-foot">
          <span>{clean(snapshot.project.name)} / {clean(sheet.name)} / detail</span>
          <span>Generated {new Date(snapshot.generatedAt).toLocaleDateString("en-US")}</span>
          <span>{sheet.peStamped ? CALC_EXPORT_COPY.reviewedCalculation : CALC_EXPORT_COPY.forEngineeringReview}</span>
        </footer>
      </article>
    </>
  );
}

function ProjectCover({ snapshot }: { snapshot: CalcExportSnapshot }) {
  const failing = snapshot.sheets.filter((sheet) => sheet.passes === false).length;
  const draft = snapshot.sheets.filter((sheet) => sheet.status === "draft").length;
  return (
    <article className="ce-cover">
      <div className="ce-cover-brand">FIRMARK</div>
      <div className="ce-cover-title"><span>Structural calculation report</span><h1>{clean(snapshot.project.name)}</h1><p>{clean(snapshot.project.address)}</p></div>
      <div className="ce-cover-details">
        <div><span>Job number</span><strong>{clean(snapshot.project.jobNumber)}</strong></div>
        <div><span>Client</span><strong>{clean(snapshot.project.client)}</strong></div>
        <div><span>Engineer</span><strong>{clean(snapshot.project.engineer)}</strong></div>
        <div><span>License</span><strong>{clean(snapshot.project.license)}</strong></div>
        <div><span>Code basis</span><strong>{clean(snapshot.project.buildingCode)}</strong></div>
        <div><span>Risk category</span><strong>{clean(snapshot.project.riskCategory)}</strong></div>
      </div>
      <div className="ce-cover-summary"><strong>{snapshot.sheets.length}</strong><span>calculations</span><strong>{failing}</strong><span>failing</span><strong>{draft}</strong><span>draft</span></div>
      <p className="ce-cover-note">{CALC_EXPORT_COPY.coverDisclaimer}</p>
    </article>
  );
}

function ProjectToc({ snapshot }: { snapshot: CalcExportSnapshot }) {
  const groupMap = new Map((snapshot.grouping.groups ?? []).map((group) => [group.id, group.label]));
  const assignments = snapshot.grouping.assignments ?? {};
  const pageSize = 22;
  const pages = snapshot.sheets.length
    ? Array.from({ length: Math.ceil(snapshot.sheets.length / pageSize) }, (_, index) => snapshot.sheets.slice(index * pageSize, (index + 1) * pageSize))
    : [[]];
  return (
    <>
      {pages.map((sheets, pageIndex) => (
        <article className="ce-toc" key={pageIndex}>
          <header><strong>FIRMARK</strong><span>Report contents {pages.length > 1 ? `${pageIndex + 1} / ${pages.length}` : ""}</span></header>
          <h1>{pageIndex ? "Calculation index – continued" : "Calculation index"}</h1>
          <table><thead><tr><th>Mark</th><th>Calculation</th><th>Group</th><th>Status</th><th>Governing</th></tr></thead><tbody>
            {sheets.map((sheet) => <tr key={sheet.id}><td>{clean(sheet.name)}</td><td>{CALC_EXPORT_REGISTRY[sheet.calculatorId]?.title ?? humanize(sheet.calculatorId)}</td><td>{clean(groupMap.get(assignments[sheet.id]) || "Ungrouped")}</td><td>{statusLabel(sheet)}</td><td>{sheet.results?.structured?.loadCalculation?.governingLabel ? clean(sheet.results.structured.loadCalculation.governingLabel) : sentenceLabel(sheet.governing)}</td></tr>)}
          </tbody></table>
        </article>
      ))}
    </>
  );
}

export function CalcExportDocument({
  snapshot,
  includeSheetPages = true,
}: {
  snapshot: CalcExportSnapshot;
  includeSheetPages?: boolean;
}) {
  const projectDocument = snapshot.scope === "project";
  return (
    <main className={`ce-document is-${snapshot.mode}`}>
      {projectDocument && snapshot.options.includeCover ? <ProjectCover snapshot={snapshot} /> : null}
      {projectDocument && snapshot.options.includeToc ? <ProjectToc snapshot={snapshot} /> : null}
      {includeSheetPages ? snapshot.sheets.map((sheet) => <CalcExportSheet key={sheet.id} snapshot={snapshot} sheet={sheet} />) : null}
    </main>
  );
}

export function exportLayoutOverflowIds(root: ParentNode = document): string[] {
  return Array.from(root.querySelectorAll<HTMLElement>(".ce-sheet[data-one-page='true']"))
    .filter((sheet) => sheet.getBoundingClientRect().height > (11 * 96) + 2)
    .map((sheet) => sheet.dataset.sheetId ?? "unknown");
}

export type { CalcStructuredValue };
